import { APIClient } from 'infra/APIClient'
import { APIConfig, AipCursorPaged } from 'types/definitions'
import { rethrowRequestError } from 'utils/error-util'
import { CancellationToken } from 'infra/cancellation'
import { getHttpErrorConstructorForStatusCode } from 'fejl'
import {
  ListProHistoricalTransactionsRequest,
  ProHistoricalTransactionResource,
  DownloadProHistoricalTransactionsAsCsvRequest,
} from './dtos/pro-transactions'
import { ProPayoutSummaryResource } from './dtos/pro-analytics'
import {
  PayoutMethodResource,
  PayoutMethodStatusResource,
} from './dtos/payout-method'

/**
 * APIs for working with payouts in the BigMoney service.
 */
export interface BigMoneyPayoutsAPI {
  payoutMethods: PayoutMethodAPI
  transactions: ProTransactionAPI
  analytics: ProAnalyticsAPI
}

/**
 * Creates a new BigMoneyPayoutsAPI.
 */
export function createBigMoneyPayoutsAPI(opts: APIConfig): BigMoneyPayoutsAPI {
  return {
    payoutMethods: new PayoutMethodAPI(opts),
    transactions: new ProTransactionAPI(opts),
    analytics: new ProAnalyticsAPI(opts),
  }
}

/*
 * API for working with payout methods.
 */
export class PayoutMethodAPI extends APIClient {
  async getByUserPublicId(userPublicId: string): Promise<PayoutMethodResource> {
    return this.client
      .get(`/payouts/v1/users/${userPublicId}/payout-method`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async getStatusByUserPublicId(
    userPublicId: string,
  ): Promise<PayoutMethodStatusResource> {
    return this.client
      .get(`/payouts/v1/users/${userPublicId}/payout-method/status`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  /*
   * Connect stripe method account with a payout method.
   */
  async connectStripe(userPublicId: string, code: string): Promise<void> {
    await this.client
      .post(`/payouts/v1/users/${userPublicId}/payout-method/connect/stripe`, {
        code,
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/*
 * API for working with pro payout analytics.
 */
export class ProAnalyticsAPI extends APIClient {
  /**
   * Gets the current user's payout summary.
   */
  async getProPayoutSummary(): Promise<ProPayoutSummaryResource> {
    return this.client
      .get(`/payouts/v1/analytics/summary`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API for working with transactions (Pro perspective).
 */
export class ProTransactionAPI extends APIClient {
  listHistoricalTransactions(
    request: ListProHistoricalTransactionsRequest,
    cancellationToken = CancellationToken.default,
  ): Promise<AipCursorPaged<ProHistoricalTransactionResource>> {
    return this.client
      .get('/payouts/v1/transactions', {
        signal: cancellationToken.asAbortSignal(),
        params: {
          page_size: request.page_size,
          page_token: request.page_token,
          order_by: request.order_by,
          search_text: request.search_text,
          transaction_source: request.transaction_source,
          date_completed_from: request.date_completed_from?.toISOString(),
          date_completed_to: request.date_completed_to?.toISOString(),
          workspace_ids: request.workspace_ids,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async downloadHistoricalTransactionsAsCsv(
    request: DownloadProHistoricalTransactionsAsCsvRequest,
  ): Promise<ReadableStream<Uint8Array>> {
    // Need to use `fetch` to get a stream.
    const params = new URLSearchParams({
      ...(request.search_text && { search_text: request.search_text }),
      ...(request.workspace_ids && { workspace_ids: request.workspace_ids }),
      ...(request.transaction_source && {
        transaction_source: request.transaction_source,
      }),
      ...(request.date_completed_from && {
        date_completed_from: request.date_completed_from.toISOString(),
      }),
      ...(request.date_completed_to && {
        date_completed_to: request.date_completed_to.toISOString(),
      }),
    })
    const response = await fetch(
      `${this.config.baseURL}/payouts/v1/transactions.csv?${params}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    )

    if (response.status !== 200) {
      const message = await response.json().then((m) => m.message)
      const ErrorClass = getHttpErrorConstructorForStatusCode(response.status)
      throw new ErrorClass(message)
    }

    return response.body!
  }
}
